import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { OfflineQueueStore } from '@cuito/api-client';
import { syncOperations } from '../api/sync';
import { readDeviceId } from '../lib/prefs';
import {
  asSyncOperation,
  browserOfflineQueue,
  notifyOfflineQueueChanged,
  offlineQueueRevision,
  subscribeOfflineQueue,
} from './queue';

/**
 * 离线队列的**读出 + 同步**（`/cashier/offline` 页与状态条的待上传计数共用这一处）。
 *
 * ## 五条不能改的口径
 *
 * ① **入队/出队只有一条路**：同步走 `OfflineQueueStore.syncPendingOrders`（逐条、串行）。
 *    这里不自己 for 循环，因为「一条失败不影响后续条目」这条语义已经由它实现并有单测
 *    （`packages/api-client/__tests__/offlineQueue.spec.ts` 第 4 条）。
 * ② **服务端的五种 `status` 在这里被折叠成两件事**（契约 §18.2 的「前端行为」列）：
 *    `APPLIED` / `DUPLICATE` ⇒ 从本地队列移除；`CONFLICT` / `SKIPPED` / `NOT_FOUND` ⇒
 *    **抛错**，于是 `syncPendingOrders` 把它留在队列里并计入 `failed`。这正是
 *    「保留本地记录供对账，显著提示店员」（`REQ-CASHIER-023`/`024`）——
 *    若把冲突也当成功删掉，账目就对不上了，而且**没有任何界面会告诉店员**。
 * ③ **进度是「已发送/总数」**：`sync-progress` 的 `同步中…（N/M）` 里 M 是**开始同步时**
 *    队列的长度（分母会变的话进度会倒退），N 在每条**发送之后**自增（失败的那条也算）。
 * ④ **载荷必须过 `asSyncOperation`**：队列里是 `unknown`，读不出来的一条按失败处理并留在队列里，
 *    绝不让页面在渲染 `op.orderNo` 时抛异常白屏。
 * ⑤ **刷新靠订阅、不轮询**（§6.8 实现要点 2）：`subscribeOfflineQueue` 的修订号一变就重读。
 *
 * ## 注入缝
 *
 * `store` 参数（`undefined` = 用浏览器的那一个，`null` = 本机没有队列）是给测试用的：
 * jsdom 没有 `indexedDB`，而「没有队列」与「队列是空的」在界面上必须是两句不同的话。
 */
export interface OfflineQueueItemView {
  /** 队列条目 id（IndexedDB 的主键）—— 页面的 `data-op-id` 用它 */
  id: string;
  status: 'PENDING' | 'FAILED';
  /** 失败原因（服务端原话；读不出来的记录给一句本机的话） */
  message?: string;
  /** 该条目的操作号（`SyncOperationInput.opId`）；读不出来时为 `null` */
  opId: string | null;
  /** 操作类型（`ACCEPT_ORDER` / `PICK_LINES` …）；读不出来时为 `null` */
  type: string | null;
  orderNo: string | null;
}

export interface OfflineQueueView {
  /** 本机是否有可用的队列库（`false` ⇒ 页面必须**如实**说，而不是显示「没有待上传的记录」） */
  supported: boolean;
  items: OfflineQueueItemView[];
  syncing: boolean;
  progress: { done: number; total: number };
  refresh: () => Promise<void>;
  sync: () => Promise<void>;
}

/** 失败条目上没有服务端原话时的兜底文案（`05-ui-design-system.md` §3.3 的逐字文案） */
export const SYNC_FAILED_HINT = '上传失败，点击重试';

/** 队列里那条记录读不出来（被写坏 / 别的版本写的）：留在队列里，用一句人话说清楚 */
export const CORRUPT_ITEM_HINT = '这条记录读不出来，请先联网后交给技术支持核对';

export function useOfflineQueue(store?: OfflineQueueStore | null): OfflineQueueView {
  // `undefined` 是「用浏览器那一个」；显式 `null` 是「本机没有队列」（测试与不支持 IDB 的环境）
  const queue = useMemo<OfflineQueueStore | null>(
    () => (store === undefined ? browserOfflineQueue() : store),
    [store],
  );
  const revision = useSyncExternalStore(subscribeOfflineQueue, offlineQueueRevision, offlineQueueRevision);

  const [items, setItems] = useState<OfflineQueueItemView[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  /** 失败原因按**条目 id** 记住：重读队列（刷新 / 新入队）之后徽标仍然是「上传失败」 */
  const failures = useRef(new Map<string, string>());

  const toViews = useCallback(async (): Promise<OfflineQueueItemView[]> => {
    if (queue === null) return [];
    const rows = await queue.getPendingOrders();
    return rows.map((row) => {
      const op = asSyncOperation(row.payload);
      const message = failures.current.get(row.id);
      return {
        id: row.id,
        status: message === undefined ? 'PENDING' : 'FAILED',
        ...(message === undefined ? {} : { message }),
        opId: op?.opId ?? null,
        type: op?.type ?? null,
        orderNo: op?.orderNo ?? null,
      };
    });
  }, [queue]);

  const refresh = useCallback(async (): Promise<void> => {
    setItems(await toViews());
  }, [toViews]);

  useEffect(() => {
    void refresh().catch(() => setItems([]));
  }, [refresh, revision]);

  /**
   * 同步（「立即同步」按钮与恢复联网都调它）。
   *
   * `syncingRef` 是**防重入**的：连点两次会发出两批相同操作 —— 服务端有派生幂等
   * （§18.2 的 A-P9-11）所以钱不会收两遍，但队列会收到两份结果、界面上的进度会来回跳。
   */
  const syncingRef = useRef(false);
  const sync = useCallback(async (): Promise<void> => {
    if (queue === null || syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      const total = (await queue.getPendingOrders()).length;
      setProgress({ done: 0, total });
      /** 本次同步里每条操作的失败原话（键是 `opId` —— `sendFn` 只拿得到载荷） */
      const messages = new Map<string, string>();

      await queue.syncPendingOrders(async (payload) => {
        const op = asSyncOperation(payload);
        try {
          if (op === null) throw new Error(CORRUPT_ITEM_HINT);
          const response = await syncOperations(readDeviceId(), [op]);
          const result = response.results.find((entry) => entry.opId === op.opId);
          // 服务端没回声这条操作 ⇒ 保守处理：留在队列里（宁可重复上传，不可静默丢失）
          if (result === undefined) throw new Error(SYNC_FAILED_HINT);
          // APPLIED / DUPLICATE ⇒ 返回（成功，由 syncPendingOrders 删除这一条）
          if (result.status === 'APPLIED' || result.status === 'DUPLICATE') return;
          // CONFLICT / SKIPPED / NOT_FOUND ⇒ 抛（留在队列里供对账，并把原因显示出来）
          throw new Error(result.message ?? SYNC_FAILED_HINT);
        } catch (error) {
          if (op !== null) {
            messages.set(op.opId, error instanceof Error && error.message !== '' ? error.message : SYNC_FAILED_HINT);
          }
          throw error;
        } finally {
          setProgress((current) => ({ ...current, done: current.done + 1 }));
        }
      });

      // 同步完再读一次：留在队列里的就是失败的（`syncPendingOrders` 只删成功的那些）
      const left = await toViews();
      const nextFailures = new Map<string, string>();
      for (const row of left) {
        const message =
          row.opId === null
            ? CORRUPT_ITEM_HINT
            : (messages.get(row.opId) ?? failures.current.get(row.id) ?? SYNC_FAILED_HINT);
        nextFailures.set(row.id, message);
      }
      failures.current = nextFailures;
    } finally {
      syncingRef.current = false;
      setSyncing(false);
      notifyOfflineQueueChanged();
    }
  }, [queue, toViews]);

  return {
    supported: queue !== null,
    items,
    syncing,
    progress,
    refresh,
    sync,
  };
}
